import React, { useState } from 'react';
import { AuthLayout } from '../layouts/AuthLayout';
import { AuthInput } from '../components/AuthInput';
import { AuthButton } from '../components/AuthButton';
import { GenderSelector } from '../components/GenderSelector';
import { Link, useNavigate } from 'react-router-dom';
import { authService, RegisterPayload } from '../services/authService';
import { message } from 'antd'; // Assuming antd is installed as per package.json

import { useTenant } from '@/features/tenant/context/TenantContext';

export const RegisterPage: React.FC = () => {
    const navigate = useNavigate();
    const { tenantCode } = useTenant();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState<RegisterPayload>({
        email: '',
        password: '',
        confirmPassword: '',
        realName: '',
        englishName: '',
        gender: 'secret',
        phone: '',
        inviteCode: tenantCode || '' // Pre-fill from context
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleGenderChange = (value: string) => {
        setFormData({ ...formData, gender: value });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (formData.password !== formData.confirmPassword) {
            message.error("Passwords do not match");
            return;
        }

        setLoading(true);
        try {
            const response = await authService.register(formData);
            if (response.success) {
                message.success("Registration successful! Please login.");
                navigate(`/${tenantCode}/login`); // Navigate within tenant
            }
        } catch (error: any) {
            const msg = error.response?.data?.error || error.message || "Registration failed";
            message.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthLayout
            title="创建账号"
            subtitle="加入我们，探索未来"
        >
            <form onSubmit={handleSubmit}>
                {/* Section 1: Credentials */}
                <AuthInput
                    name="email"
                    type="email"
                    placeholder="电子邮箱 (Email)"
                    label="电子邮箱"
                    value={formData.email}
                    onChange={handleChange}
                    required
                />

                <AuthInput
                    name="password"
                    type="password"
                    placeholder="登录密码"
                    label="登录密码"
                    value={formData.password}
                    onChange={handleChange}
                    required
                />
                <AuthInput
                    name="confirmPassword"
                    type="password"
                    placeholder="确认密码"
                    label="确认密码"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                />

                {/* Section 2: Personal Info */}
                <AuthInput
                    name="englishName"
                    type="text"
                    placeholder="英文名 / English Name"
                    label="英文名"
                    value={formData.englishName}
                    onChange={handleChange}
                    required
                />
                <div>
                    <AuthInput
                        name="realName"
                        type="text"
                        placeholder="真实姓名 (中文)"
                        label="真实姓名"
                        value={formData.realName}
                        onChange={handleChange}
                        required
                    />
                    <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)', marginTop: '-1rem', marginBottom: '1.5rem', paddingLeft: '0.5rem' }}>
                        * 请务必填写真实姓名，后续身份验证不一致将被封禁
                    </div>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                    <GenderSelector
                        label="性别"
                        value={formData.gender}
                        onChange={handleGenderChange}
                    />
                </div>

                {/* Section 3: Contact */}
                <AuthInput
                    name="phone"
                    type="tel"
                    placeholder="手机号码"
                    label="手机号码"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                />

                <div>
                    <label className="auth-label-static">邀请码</label>
                    <AuthInput
                        name="inviteCode"
                        type="text"
                        placeholder="邀请码"
                        value={formData.inviteCode}
                        onChange={handleChange}
                        readOnly // Lock the field
                        disabled // Visual indication
                        required
                    />
                </div>

                <AuthButton type="submit" style={{ marginTop: '0.5rem' }} loading={loading}>
                    立即注册
                </AuthButton>
            </form>

            <div style={{ textAlign: 'center', marginTop: '1rem', color: 'var(--auth-text-secondary)', paddingBottom: '1rem' }}>
                已经有账号？{' '}
                <Link to={`/${tenantCode}/login`} style={{ color: 'var(--auth-accent-secondary)', textDecoration: 'none', fontWeight: 600 }}>
                    去登录
                </Link>
            </div>
        </AuthLayout>
    );
};
